import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

const STORAGE_KEY = 'tickets:mobileV2'

/**
 * Query/localStorage-mirrored view params for the tickets page.
 *
 * Phase 5: mobile-v2 is now ON by default. The new responsive layout
 * is what every phone and tablet user sees. `?mobile-v2=0` is the
 * escape hatch: it stores `0` in localStorage so subsequent
 * SPA-internal navigations remember the opt-out.
 *
 * `?mobile-v2=1` is still accepted as an explicit force-on — useful
 * to clear a previously-stored opt-out without manually emptying
 * localStorage.
 *
 * No URL param → read localStorage. Stored '0' → off. Anything else
 * (including the absence of a stored value) → on (default).
 *
 * Backwards-compat: users who opted in pre-Phase 5 had '1' stored;
 * stored !== '0' keeps them on without a fresh visit needed.
 *
 * Wrapped in useMemo on `search` so consumers don't re-render on
 * unrelated route changes.
 */
function useTicketsViewParams() {
  const { search } = useLocation()
  return useMemo(() => {
    const params = new URLSearchParams(search)
    const urlValue = params.get('mobile-v2')
    if (urlValue === '0') {
      try { localStorage.setItem(STORAGE_KEY, '0') } catch { /* storage blocked: fall back to the default */ }
      return { mobileV2: false }
    }
    if (urlValue === '1') {
      try { localStorage.removeItem(STORAGE_KEY) } catch { /* storage blocked: fall back to the default */ }
      return { mobileV2: true }
    }
    let stored = null
    try { stored = localStorage.getItem(STORAGE_KEY) } catch { /* storage blocked: fall back to the default */ }
    return { mobileV2: stored !== '0' }
  }, [search])
}

export default useTicketsViewParams
